import { useSignal, useComputed } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import type { ComponentChildren } from 'preact'

interface CarouselProps {
  children: ComponentChildren[]
  initialIndex?: number
  onIndexChange?: (index: number) => void
  className?: string
}

export function Carousel({
  children,
  initialIndex = 0,
  onIndexChange,
  className = '',
}: CarouselProps) {
  const currentIndex = useSignal(initialIndex)
  const scrollerRef = useRef<HTMLDivElement>(null)

  const canGoPrev = useComputed(() => currentIndex.value > 0)
  const canGoNext = useComputed(() => currentIndex.value < children.length - 1)

  const goToIndex = (index: number) => {
    if (index >= 0 && index < children.length && scrollerRef.current) {
      const scroller = scrollerRef.current
      const itemWidth = scroller.clientWidth
      scroller.scrollTo({
        left: index * itemWidth,
        behavior: 'smooth',
      })
    }
  }

  const goNext = () => canGoNext.value && goToIndex(currentIndex.value + 1)
  const goPrev = () => canGoPrev.value && goToIndex(currentIndex.value - 1)

  // Handle scroll events to update current index
  const handleScroll = () => {
    if (!scrollerRef.current) return

    const scroller = scrollerRef.current
    const itemWidth = scroller.clientWidth
    const scrollLeft = scroller.scrollLeft
    const newIndex = Math.round(scrollLeft / itemWidth)

    if (newIndex !== currentIndex.value && newIndex >= 0 && newIndex < children.length) {
      currentIndex.value = newIndex
      onIndexChange?.(newIndex)
    }
  }

  // Throttled scroll handler to improve performance
  const throttledHandleScroll = (() => {
    let ticking = false
    return () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }
  })()

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    // Set initial scroll position
    const itemWidth = scroller.clientWidth
    scroller.scrollLeft = initialIndex * itemWidth

    // Keyboard events
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
    }

    // Scroll event listener
    scroller.addEventListener('scroll', throttledHandleScroll, { passive: true })
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      scroller.removeEventListener('scroll', throttledHandleScroll)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <div className={`relative ${className}`}>
      <div
        ref={scrollerRef}
        className="flex gap-10 overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children.map((child, index) => (
          <div key={index} className="flex-shrink-0 w-full snap-start">
            {child}
          </div>
        ))}
      </div>
    </div>
  )
}
